/**
 * Sync API Client
 *
 * API calls for employee synchronization with MySQL.
 */

import { apiRequest } from './api';

export interface SyncStatusEmployee {
  employeeId: number;
  userId: number;
  employeeName: string;
  currentSyncStatus: 'active' | 'inactive' | 'archived';
  mysqlUserActive: boolean | null;
  lastSyncedAt: string | null;
  needsSync: boolean;
}

export interface SyncStatusResponse {
  summary: {
    total: number;
    needsSync: number;
    active: number;
    inactive: number;
    archived: number;
  };
  employees: SyncStatusEmployee[];
}

export interface MySQLUser {
  id: number;
  email: string;
  name: string;
  active: boolean;
}

export interface NewUsersResponse {
  count: number;
  users: MySQLUser[];
}

export interface SyncResult {
  timestamp: string;
  summary: {
    totalMySQLUsers: number;
    totalEmployees: number;
    newUsersAvailable: number;
    syncedToActive: number;
    syncedToInactive: number;
    syncedToArchived: number;
    createdProfiles: number;
  };
  details: {
    newUsersAvailable: MySQLUser[];
    activatedEmployees: number[];
    deactivatedEmployees: number[];
    archivedEmployees: number[];
    createdEmployees: Array<{ employeeId: number; userId: number; userName: string }>;
  };
}

export interface SyncPreviewItem {
  employeeId: number;
  userId: number;
  employeeName: string;
  currentSyncStatus: 'active' | 'inactive' | 'archived';
  proposedSyncStatus: 'active' | 'inactive' | 'archived';
  mysqlUserActive: boolean | null;
  changeDescription: string;
}

export interface NewUserPreviewItem {
  userId: number;
  userName: string;
  userEmail: string;
  changeDescription: string;
}

export interface SyncPreviewResponse {
  count: number;
  employees: SyncPreviewItem[];
  newUsersCount: number;
  newUsers: NewUserPreviewItem[];
}

/**
 * Get sync status for all employees
 */
export async function getSyncStatus(): Promise<SyncStatusResponse> {
  return apiRequest<SyncStatusResponse>('/sync/status');
}

/**
 * Get new MySQL users without employee profiles
 */
export async function getNewUsers(): Promise<NewUsersResponse> {
  return apiRequest<NewUsersResponse>('/sync/new-users');
}

/**
 * Get sync preview - shows what would change if sync is run
 */
export async function getSyncPreview(): Promise<SyncPreviewResponse> {
  return apiRequest<SyncPreviewResponse>('/sync/preview');
}

/**
 * Sync selected employees with MySQL and optionally create new profiles
 */
export async function syncSelectedEmployees(employeeIds: number[], newUserIds: number[] = []): Promise<SyncResult> {
  return apiRequest<SyncResult>('/sync', {
    method: 'POST',
    body: JSON.stringify({ employeeIds, newUserIds }),
  });
}

/**
 * Sync a single employee with MySQL
 */
export async function syncEmployee(employeeId: number): Promise<SyncStatusEmployee> {
  return apiRequest<SyncStatusEmployee>(`/sync/${employeeId}`, { method: 'POST' });
}

/**
 * Reactivate an archived or inactive employee
 */
export async function reactivateEmployee(employeeId: number): Promise<{ success: boolean; message: string }> {
  return apiRequest<{ success: boolean; message: string }>(`/sync/${employeeId}/reactivate`, { method: 'POST' });
}

export interface CleanupResult {
  removedCount: number;
  removedEmployees: Array<{
    employeeId: number;
    userId: number;
    reason: string;
  }>;
}

/**
 * Cleanup employees with invalid informationProvider
 */
export async function cleanupInvalidEmployees(): Promise<CleanupResult> {
  return apiRequest<CleanupResult>('/sync/cleanup', { method: 'POST' });
}
